// src/themes/modern/menu/components/CategoryMenu.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaChevronRight, FaChevronLeft } from "react-icons/fa";
import MenuItemGrid from "./MenuItemGrid";
import MenuItemList from "./MenuItemList";
import type {
  BranchData,
  Category,
  MainCategory,
  MenuItem,
} from "~/types/new_types";
import {
  getCategoryDescription,
  getCategoryName,
} from "~/utils/categoryHelper";
import { useBranch } from "~/contexts/BranchContext";
import CategoryHeader from "./CategoryHeader";

interface CategoryMenuProps {
  category: MainCategory;
  viewMode: "grid" | "list";
  setSelectedItem: React.Dispatch<React.SetStateAction<MenuItem | null>>;
  SubcategoryStyle?: "tabs" | "dropdown";
}

const CategoryMenu: React.FC<CategoryMenuProps> = ({
  category,
  viewMode,
  setSelectedItem,
  SubcategoryStyle,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  // const subCategories = branchData?.categories.filter(cat => cat.parent_id === category.id);
  const [activeSubCategory, setActiveSubCategory] = useState(
    category.subCategories.filter((sub) => sub.is_published).length
      ? category.subCategories.filter((sub) => sub.is_published)[0].id
      : null
  );
  const [activeSubCategories, setActiveSubCategories] = useState<boolean[]>(
    category.subCategories.filter((sub) => sub.is_published).map((item) => true)
  );
  const [expandedSubCategories, setExpandedSubCategories] = useState<
    Record<string, boolean>
  >({});
  const [isExpanded, setIsExpanded] = useState(true);

  const { branchData } = useBranch();
  const itemsSectionRef = React.useRef<HTMLDivElement | null>(null);
  const scrollToItemsSection = React.useCallback(() => {
    itemsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const hasScrolledOnViewMode = React.useRef(false);

  // Check if this category has items directly assigned
  const hasDirectItems = category.items && category.items.length > 0;

  // Get direct items for this category
  const directItems = category.items || [];

  // Check if category has published subcategories with items
  const hasSubcategoriesWithItems = category.subCategories.some(
    (sub) => sub.is_published && sub.items && sub.items.length > 0
  );

  React.useEffect(() => {
    // Initialize the first subcategory as expanded in dropdown mode
    const publishedSubCategories = category.subCategories.filter(
      (sub) => sub.is_published
    );
    if (
      SubcategoryStyle === "dropdown" &&
      publishedSubCategories.length > 0
    ) {
      setExpandedSubCategories({ [publishedSubCategories[0].id]: true });
    }
  }, [SubcategoryStyle, category.subCategories]);

  React.useEffect(() => {
    if (hasScrolledOnViewMode.current) {
      scrollToItemsSection();
    } else {
      hasScrolledOnViewMode.current = true;
    }
  }, [viewMode, scrollToItemsSection]);

  const toggleSubCategory = (subCategoryId: string) => {
    setExpandedSubCategories((prev) => ({
      ...prev,
      [subCategoryId]: !prev[subCategoryId],
    }));
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, height: 0, overflow: "hidden" },
    visible: { opacity: 1, height: "auto", overflow: "visible" },
  };

  // Render tabs style
  const renderTabsStyle = () => (
    <>
      {/* Subcategory navigation */}
      <div className="mb-8 overflow-x-auto no-scrollbar">
        <div className="flex gap-3 py-2">
          {category.subCategories
            .filter((subCategory) => subCategory.is_published)
            .map((subCategory) => {
              const subCategoryItems =
                branchData?.items?.filter(
                  (item) => item.category_id === subCategory.id
                ) ?? [];
              return (
                <motion.button
                  key={subCategory.id}
                  onClick={() => {
                    setActiveSubCategory(subCategory.id);
                    scrollToItemsSection();
                  }}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 flex items-center gap-2 whitespace-nowrap cursor-pointer transition-all duration-300"
                  style={{
                    backgroundColor:
                      activeSubCategory === subCategory.id
                        ? branchData?.branch.theme.primaryColor
                        : `${branchData?.branch.theme.primaryColor}15`,
                    color:
                      activeSubCategory === subCategory.id
                        ? "#fff"
                        : branchData?.branch.theme.textColor,
                    borderRadius: branchData?.branch.theme.borderRadius,
                    // boxShadow: activeSubCategory === subCategory.id
                    // 	? `0 8px 16px ${branchData?.branch.theme.primaryColor}30`
                    // 	: 'none'
                  }}
                >
                  {getCategoryName(subCategory, i18n.language)}
                  {subCategoryItems.length > 0 && (
                    <span
                      className="px-1.5 py-0.5 text-xs rounded-full inline-block"
                      style={{
                        backgroundColor:
                          activeSubCategory === subCategory.id
                            ? "rgba(255, 255, 255, 0.3)"
                            : `${branchData?.branch.theme.primaryColor}20`,
                      }}
                    >
                      {subCategoryItems.length}
                    </span>
                  )}
                </motion.button>
              );
            })}
        </div>
      </div>

      {/* Show content for active subcategory */}
      <AnimatePresence mode="wait">
        {category.subCategories.map((subCategory) => {
          // const subCategoryItems = branchData?.items.filter(item => item.category_id === subCategory.id)
          return (
            activeSubCategory === subCategory.id && (
              <motion.div
                key={subCategory.id}
              // variants={containerVariants}
              // initial="hidden"
              // animate="visible"
              // exit="hidden"
              >
                {viewMode === "grid" ? (
                  <MenuItemGrid
                    items={subCategory.items}
                    theme={branchData?.branch.theme!}
                    setSelectedItem={setSelectedItem}
                  />
                ) : (
                  <MenuItemList
                    items={subCategory.items}
                    theme={branchData?.branch.theme!}
                    setSelectedItem={setSelectedItem}
                  />
                )}
              </motion.div>
            )
          );
        })}
      </AnimatePresence>
    </>
  );

  // Render dropdown style
  const renderDropdownStyle = () => (
    <div className="space-y-6">
      {category.subCategories
        .filter((subCategory) => subCategory.is_published)
        .map((subCategory, index) => {
          // const subCategoryItems = branchData?.items.filter(item => item.category_id === subCategory.id)
          return (
            <motion.div
              key={subCategory.id}
              className=" overflow-hidden"
              style={{
                backgroundColor: `${branchData?.branch.theme.cardColor}70`,
                borderRadius: branchData?.branch.theme.borderRadius,
                boxShadow: `0 4px 12px ${branchData?.branch.theme.primaryColor}10`,
              }}
              whileHover={{ y: -3 }}
              transition={{ duration: 0.3 }}
            >
              <motion.button
                // onClick={() => setActiveSubCategory(
                // 	activeSubCategory === subCategory.id ? '' : subCategory.id
                // )}
                onClick={() => {
                  setActiveSubCategories((prev) =>
                    prev.map((value, idx) => (idx === index ? !value : value))
                  );
                  scrollToItemsSection();
                }}
                className="w-full px-4 py-4 flex items-center justify-between cursor-pointer"
                whileHover={{
                  backgroundColor: `${branchData?.branch.theme.primaryColor}10`,
                }}
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{
                      rotate: activeSubCategories[index]
                        ? 90 * (isRTL ? -1 : 1)
                        : 0,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {isRTL ? <FaChevronLeft /> : <FaChevronRight />}
                  </motion.div>
                  <span
                    className="text-lg font-medium"
                    style={{ color: branchData?.branch.theme.textColor }}
                  >
                    {getCategoryName(subCategory, i18n.language)}
                  </span>
                </div>
                <div
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: `${branchData?.branch.theme.primaryColor}15`,
                    color: branchData?.branch.theme.textColor,
                  }}
                >
                  {subCategory.itemsCount}{" "}
                  {subCategory.itemsCount === 1
                    ? t("menu.item")
                    : t("menu.items")}
                </div>
              </motion.button>

              <AnimatePresence>
                {activeSubCategories[index] && (
                  <motion.div
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    transition={{ duration: 0.4 }}
                  >
                    <div className="p-4">
                      {viewMode === "grid" ? (
                        <MenuItemGrid
                          items={subCategory.items}
                          theme={branchData?.branch.theme!}
                          setSelectedItem={setSelectedItem}
                        />
                      ) : (
                        <MenuItemList
                          items={subCategory.items}
                          theme={branchData?.branch.theme!}
                          setSelectedItem={setSelectedItem}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
    </div>
  );

  return (
    <div
      className="mb-16 overflow-hidden"
      style={{
        borderRadius: branchData?.branch.theme.borderRadius,
        backgroundColor: `${branchData?.branch.theme.cardColor}50`,
      }}
    >
      {/* {renderCategoryHeader()} */}
      <CategoryHeader category={category} />

      <div
        ref={itemsSectionRef}
        className="px-2 md:px-4 pb-6"
        style={{ scrollMarginTop: "120px" }}
      >
        {/* If category has direct items and no subcategories, show items directly */}
        {hasDirectItems && !hasSubcategoriesWithItems ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mt-4"
          >
            {viewMode === "grid" ? (
              <MenuItemGrid
                items={directItems}
                theme={branchData?.branch.theme!}
                setSelectedItem={setSelectedItem}
              />
            ) : (
              <MenuItemList
                items={directItems}
                theme={branchData?.branch.theme!}
                setSelectedItem={setSelectedItem}
              />
            )}
          </motion.div>
        ) : hasDirectItems && hasSubcategoriesWithItems ? (
          // Show both direct items and subcategory navigation
          <>
            {/* Direct items section */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="mb-8"
            >
              <h3
                className="text-lg font-medium mb-4"
                style={{ color: branchData?.branch.theme.textColor }}
              >
                {getCategoryName(category, i18n.language)}
              </h3>
              {viewMode === "grid" ? (
                <MenuItemGrid
                  items={directItems}
                  theme={branchData?.branch.theme!}
                  setSelectedItem={setSelectedItem}
                />
              ) : (
                <MenuItemList
                  items={directItems}
                  theme={branchData?.branch.theme!}
                  setSelectedItem={setSelectedItem}
                />
              )}
            </motion.div>

            {/* Subcategories section */}
            <div className="border-t pt-6" style={{ borderColor: `${branchData?.branch.theme.primaryColor}20` }}>
              {SubcategoryStyle === "tabs" && renderTabsStyle()}

              {SubcategoryStyle === "dropdown" &&
                renderDropdownStyle()}
            </div>
          </>
        ) : (
          // Show only subcategory navigation for categories with subcategories only
          <>
            {SubcategoryStyle === "tabs" && renderTabsStyle()}

            {SubcategoryStyle === "dropdown" &&
              renderDropdownStyle()}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryMenu;
